import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { COLOR_PALETTE, MATERIAL_BRANDS, COMPANY } from "@/lib/site-data";

export default function PonyvakSzinek() {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F8FAFC] to-white" />
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#EA580C]/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="eyebrow">Ponyvák és színek</span>
            <h1 className="mt-3 font-heading font-extrabold text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Többféle szín.
            </h1>
            <p className="mt-5 text-gray-600 text-base sm:text-lg leading-relaxed max-w-2xl">
              Belga, német, osztrák és spanyol PVC ponyvákkal dolgozunk, többféle
              g/m² súlyban. Alább raktári színeink szerepelnek, de igény esetén
              tudunk rendelni más színeket is, amennyiben elérhető
              beszállítóinknál. A minták telephelyünkön megtekinthetőek.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Color palette grid */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
            {COLOR_PALETTE.map((c, i) => {
              const isLight = ["#ffffff", "#f5f5dc", "#facc15", "#cbd5e1", "#d4c5a0"].includes(c.hex);
              return (
                <motion.div
                  key={c.code}
                  initial={{ opacity: 0, scale: 0.92 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.35, delay: (i % 12) * 0.03 }}
                  className="group relative rounded-lg overflow-hidden cursor-pointer hover:scale-[1.04] transition-transform shadow-sm hover:shadow-xl"
                  data-testid={`color-swatch-${c.code}`}
                >
                  <div
                    className="aspect-square w-full"
                    style={{ backgroundColor: c.hex }}
                  />
                  <div
                    className={`absolute inset-x-0 bottom-0 p-2.5 text-[11px] backdrop-blur-sm border-t ${
                      isLight
                        ? "bg-white/80 text-[#0B192C] border-gray-200"
                        : "bg-black/35 text-white border-white/10"
                    }`}
                  >
                    <div className="font-heading font-bold leading-tight">
                      {c.name}
                    </div>
                    <div className="opacity-75 mt-0.5 tracking-widest">
                      RAL {c.code}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-6 text-xs text-gray-500 text-center">
            * A monitorokon megjelenő színek tájékoztatóak. Pontos színhez
            kérjük tekintse meg a fizikai mintákat műhelyünkben.
          </div>
        </div>
      </section>

      {/* Material brands */}
      <section className="bg-[#F8FAFC] py-16 md:py-24 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <span className="eyebrow">Beszállítóink</span>
          <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl tracking-tight max-w-2xl">
            Csak prémium európai gyártóktól.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MATERIAL_BRANDS.map((b, i) => (
              <motion.div
                key={b.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white p-6 rounded-lg border border-gray-200 hover:border-[#EA580C] hover:shadow-xl transition-all relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-[#EA580C]/[0.04] rounded-full -translate-y-8 translate-x-8" />
                <div className="font-heading font-extrabold text-[#0B192C] text-2xl">
                  {b.name}
                </div>
                <div className="text-sm text-gray-500 mt-1">{b.country}</div>
                <div className="mt-5 pt-5 border-t border-gray-100 text-xs">
                  <div className="text-gray-400 uppercase tracking-widest">
                    Súly
                  </div>
                  <div className="font-semibold text-[#0B192C] mt-0.5">
                    {b.weight}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 text-[#EA580C] font-semibold">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-xs uppercase tracking-widest">Minőségi alapanyagok</span>
            </div>
            <h3 className="mt-2 font-heading font-bold text-2xl md:text-3xl">
              Színt választana? Hozzon mintát vagy referenciát.
            </h3>
            <p className="mt-2 text-gray-300 max-w-xl">
              Helyszínen több száz színből választhat.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button asChild className="bg-[#EA580C] hover:bg-[#C2410C] h-12 px-6 font-semibold">
              <Link to="/#ajanlat">Ajánlatkérés <ArrowRight className="w-4 h-4" /></Link>
            </Button>
            <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md border-2 border-white/30 hover:bg-white hover:text-[#0B192C] font-semibold transition-colors">
              <Phone className="w-4 h-4" />{COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
